import { useState } from 'react';
import { Button, NumberInput, Textarea, Text } from '@mantine/core';
import {
	BreakpointSummaryPayload,
	CreateSecurityValueOverrideDto,
	CurrencyType,
	SecurityValueOverrideField,
	UpdateSecurityValueOverrideDto,
} from '@dealstack/shared/lib/generated';
import {
	ButtonContainer,
	InfoBlock,
	InfoBlockContainer,
	InputContainer,
	OverrideModeSwitch,
	SecurityOverrideCard,
	StyledSecurityOverride,
	StyledSecurityOverrideDrawer,
} from '@dealstack/frontend/app/pages/Valuations/views/BreakpointSummary/SecurityOverride/styled';
import { formatSummaryDecimal } from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting';
import { SecurityComponentMapValue } from '@dealstack/frontend/app/pages/Valuations/views/BreakpointSummary/types';
import {
	TransValuations,
	useTranslationValuations,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { getOverrideFieldValues } from '@dealstack/frontend/app/pages/Valuations/views/BreakpointSummary/SecurityOverride/SecurityOverrideDrawer.utils';

const securityValueOverrideFieldToLabel: Record<SecurityValueOverrideField, string> = {
	[SecurityValueOverrideField.VALUE_TOTAL_ISSUED]:
		'breakpoint_summary.override_drawer.value_total_issued_text',
	[SecurityValueOverrideField.VALUE_PER_UNIT]:
		'breakpoint_summary.override_drawer.value_per_unit_text',
};

const SecurityOverride = ({
	securityInfo,
	onClose,
	currency,
	breakpointSummaryData,
	createOverride,
	updateOverride,
}: {
	securityInfo: SecurityComponentMapValue;
	onClose: () => void;
	currency: CurrencyType | undefined;
	breakpointSummaryData: BreakpointSummaryPayload | undefined;
	createOverride: (data: CreateSecurityValueOverrideDto) => Promise<void>;
	updateOverride: (overrideId: string, data: UpdateSecurityValueOverrideDto) => Promise<void>;
}) => {
	const tValuations = useTranslationValuations();
	const [overrideType, setOverrideType] = useState<SecurityValueOverrideField>(
		securityInfo.override?.overrideField ?? SecurityValueOverrideField.VALUE_TOTAL_ISSUED
	);
	const [overrideValue, setOverrideValue] = useState<number>(
		Number(securityInfo.override?.overrideValue ?? 0)
	);
	const [note, setNote] = useState<string>(securityInfo.note ?? '');
	const { name } = securityInfo;

	const { valuePerUnit, tierParticipatingValue } = getOverrideFieldValues(
		securityInfo,
		breakpointSummaryData
	);

	const [isDirty, setIsDirty] = useState<boolean>(false);

	let displayValue = overrideValue;
	if (!isDirty) {
		displayValue = (
			overrideType === SecurityValueOverrideField.VALUE_PER_UNIT
				? valuePerUnit
				: tierParticipatingValue
		).toNumber();
	}

	return (
		<StyledSecurityOverride>
			<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
				<TransValuations
					i18nKey="breakpoint_summary.override_drawer.for_security_label"
					values={{ securityName: name }}
					defaults="<for>For - </for><securityName>{{securityName}}</securityName>"
					components={{
						for: <Text size="B6Medium" />,
						securityName: <Text size="B6Bold" />,
					}}
				/>
			</div>
			<OverrideModeSwitch
				value={overrideType}
				onChange={(value: SecurityValueOverrideField) => setOverrideType(value)}
				data={Object.entries(securityValueOverrideFieldToLabel).map(([key, label]) => {
					return {
						label: tValuations(label),
						value: key,
					};
				})}
			/>
			<SecurityOverrideCard>
				<InputContainer>
					<Text variant="sans/14/600">
						{tValuations(securityValueOverrideFieldToLabel[overrideType])} ({currency})
					</Text>
					<NumberInput
						value={displayValue}
						onChange={(value) => {
							if (typeof value === 'number') {
								setOverrideValue(value);
								setIsDirty(true);
							}
						}}
						decimalScale={2}
						min={0}
						hideControls
					/>
				</InputContainer>
				<InfoBlockContainer>
					<InfoBlock>
						<Text variant="sans/14/600">
							{tValuations(
								'breakpoint_summary.override_drawer.participating_value_label'
							)}{' '}
							({currency})
						</Text>
						<Text variant="sans/17/200">
							{formatSummaryDecimal(tierParticipatingValue)}
						</Text>
					</InfoBlock>
					<InfoBlock>
						<Text variant="sans/14/600">
							{tValuations(
								'breakpoint_summary.override_drawer.value_per_security_label'
							)}{' '}
							({currency})
						</Text>
						<Text variant="sans/17/200">{formatSummaryDecimal(valuePerUnit)}</Text>
					</InfoBlock>
				</InfoBlockContainer>
				<InputContainer>
					<Text variant="sans/14/600">
						{tValuations('breakpoint_summary.override_drawer.note_label')}
					</Text>
					<Textarea
						placeholder={tValuations(
							'breakpoint_summary.override_drawer.note_placeholder'
						)}
						minRows={6}
						maxRows={16}
						autosize
						value={note}
						onChange={(e) => setNote(e.target.value)}
					/>
				</InputContainer>
			</SecurityOverrideCard>
			<ButtonContainer>
				<Button
					disabled={!displayValue}
					onClick={() => {
						const noteValue = note.trim().length > 0 ? note : undefined;

						if (securityInfo.override) {
							void updateOverride(securityInfo.override.id, {
								overrideValue: displayValue,
								overrideField: overrideType,
								note: noteValue,
							});
						} else {
							void createOverride({
								securityId: securityInfo.securityId,
								overrideValue: displayValue,
								overrideField: overrideType,
								note: noteValue,
								overrideScope: securityInfo.overrideScope,
								trancheKey: securityInfo.trancheKey,
							});
						}
					}}
				>
					{tValuations('breakpoint_summary.override_drawer.save_button')}
				</Button>
				<Button variant="subtle" onClick={onClose}>
					{tValuations('breakpoint_summary.override_drawer.cancel_button')}
				</Button>
			</ButtonContainer>
		</StyledSecurityOverride>
	);
};

export const SecurityOverrideDrawer = ({
	securityInfo,
	isOpen,
	onClose,
	currency,
	breakpointSummaryData,
	createOverride,
	updateOverride,
}: {
	securityInfo: SecurityComponentMapValue;
	isOpen: boolean;
	onClose: () => void;
	currency: CurrencyType | undefined;
	breakpointSummaryData: BreakpointSummaryPayload | undefined;
	createOverride: (data: CreateSecurityValueOverrideDto) => Promise<void>;
	updateOverride: (overrideId: string, data: UpdateSecurityValueOverrideDto) => Promise<void>;
}) => {
	const tValuations = useTranslationValuations();

	return (
		<StyledSecurityOverrideDrawer
			opened={isOpen}
			onClose={onClose}
			title={
				<Text variant="sans/25/600">
					{tValuations('breakpoint_summary.override_drawer.title')}
				</Text>
			}
			body={
				<SecurityOverride
					securityInfo={securityInfo}
					onClose={onClose}
					currency={currency}
					breakpointSummaryData={breakpointSummaryData}
					createOverride={createOverride}
					updateOverride={updateOverride}
				/>
			}
		/>
	);
};
